import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { growersAPI } from '../services/api';
import { Sprout, Plus, Edit2, Power, X } from 'lucide-react';

const emptyForm = { code: '', name: '', email: '', phone: '', sortOrder: '' };

const GrowerManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [growers, setGrowers] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchGrowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const fetchGrowers = async () => {
    try {
      setLoading(true);
      const data = await growersAPI.getAll(includeInactive);
      setGrowers(data.growers || []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch growers:', err);
      setError('Failed to load growers');
    } finally {
      setLoading(false);
    }
  };

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await growersAPI.create({
        code: form.code.trim(),
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        sortOrder: form.sortOrder === '' ? undefined : parseInt(form.sortOrder, 10),
      });
      flash('Grower added');
      setShowAddModal(false);
      setForm(emptyForm);
      fetchGrowers();
    } catch (err) {
      console.error('Add grower error:', err);
      setError(err.response?.data?.error || 'Failed to add grower');
    }
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({
      code: g.code,
      name: g.name || '',
      email: g.email || '',
      phone: g.phone || '',
      sortOrder: g.sort_order ?? '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await growersAPI.update(editing.id, {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        sortOrder: form.sortOrder === '' ? undefined : parseInt(form.sortOrder, 10),
      });
      flash('Grower updated');
      setShowEditModal(false);
      setEditing(null);
      setForm(emptyForm);
      fetchGrowers();
    } catch (err) {
      console.error('Edit grower error:', err);
      setError(err.response?.data?.error || 'Failed to update grower');
    }
  };

  const handleToggleActive = async (g) => {
    if (g.is_active) {
      if (!window.confirm(`Deactivate grower "${g.code}"?`)) return;
      try {
        await growersAPI.deactivate(g.id);
        flash('Grower deactivated');
        fetchGrowers();
      } catch (err) {
        console.error('Deactivate grower error:', err);
        setError('Failed to deactivate grower');
      }
    } else {
      try {
        await growersAPI.update(g.id, { isActive: true });
        flash('Grower reactivated');
        fetchGrowers();
      } catch (err) {
        console.error('Reactivate grower error:', err);
        setError('Failed to reactivate grower');
      }
    }
  };

  const formField = (label, field, props = {}) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
        {label}
      </label>
      <input
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          boxSizing: 'border-box',
        }}
        {...props}
      />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div style={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '16px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button onClick={() => navigate('/dashboard')} style={navBtn()}>Dashboard</button>
              <button onClick={() => navigate('/pallets')} style={navBtn()}>Pallet Sheets</button>
              <button onClick={() => navigate('/reports')} style={navBtn()}>Reports</button>
              <button onClick={() => navigate('/admin')} style={navBtn('#10b981', '600')}>Admin</button>
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              style={{
                padding: '8px 16px', backgroundColor: '#ef4444', color: 'white',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '500'
              }}
            >Logout</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '28px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Sprout size={28} color="#059669" /> Growers
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '6px 0 0 0' }}>
              Manage grower codes, display names, email and phone — the grower dropdown throughout the app reads from this list.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', color: '#6b7280' }}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <button
              onClick={() => { setForm(emptyForm); setShowAddModal(true); }}
              style={{
                padding: '10px 16px', backgroundColor: '#10b981', color: 'white',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
                fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <Plus size={16} /> Add Grower
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px'
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#d1fae5', border: '1px solid #10b981', color: '#065f46',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px'
          }}>{success}</div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
          ) : growers.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No growers yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th()}>Code</th>
                  <th style={th()}>Name</th>
                  <th style={th()}>Email</th>
                  <th style={th()}>Phone</th>
                  <th style={th()}>Status</th>
                  <th style={{ ...th(), textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {growers.map((g) => (
                  <tr key={g.id} style={{ borderBottom: '1px solid #e5e7eb', opacity: g.is_active ? 1 : 0.55 }}>
                    <td style={{ ...td(), fontWeight: 600 }}>{g.code}</td>
                    <td style={td()}>{g.name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={td()}>{g.email || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={td()}>{g.phone || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={td()}>
                      <span style={{
                        padding: '3px 8px', borderRadius: '12px', fontSize: '11px',
                        backgroundColor: g.is_active ? '#d1fae5' : '#f3f4f6',
                        color: g.is_active ? '#065f46' : '#6b7280'
                      }}>{g.is_active ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={{ ...td(), textAlign: 'right' }}>
                      <button onClick={() => openEdit(g)} style={iconBtn('#3b82f6')} title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(g)}
                        style={iconBtn(g.is_active ? '#ef4444' : '#10b981')}
                        title={g.is_active ? 'Deactivate' : 'Reactivate'}
                      >
                        <Power size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(showAddModal || showEditModal) && (
        <div style={modalBackdrop()}>
          <div style={modalCard()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px' }}>
                {showAddModal ? 'Add Grower' : `Edit Grower ${editing?.code}`}
              </h3>
              <button
                onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditing(null); setForm(emptyForm); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              ><X size={20} /></button>
            </div>
            <form onSubmit={showAddModal ? handleAdd : handleEdit}>
              {showAddModal && formField('Code (unique, e.g. A31)', 'code', { required: true })}
              {!showAddModal && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Code</label>
                  <div style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', borderRadius: '8px', fontSize: '14px' }}>
                    {editing?.code}
                  </div>
                </div>
              )}
              {formField('Name (display name)', 'name', { placeholder: 'e.g. Smith Farms' })}
              {formField('Email', 'email', { type: 'email', placeholder: 'grower@example.com' })}
              {formField('Phone', 'phone', { placeholder: '+61 ...' })}
              {formField('Sort order (optional)', 'sortOrder', { type: 'number' })}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditing(null); setForm(emptyForm); }}
                  style={{
                    padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#374151',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                  }}
                >Cancel</button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 16px', backgroundColor: '#10b981', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                  }}
                >{showAddModal ? 'Add' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const navBtn = (color = '#374151', weight = '500') => ({
  background: 'none', border: 'none', fontSize: '15px',
  color, cursor: 'pointer', fontWeight: weight,
});
const th = () => ({
  padding: '12px 16px', textAlign: 'left', fontWeight: '600',
  fontSize: '13px', color: '#374151',
});
const td = () => ({
  padding: '12px 16px', fontSize: '14px', color: '#111827',
});
const iconBtn = (color) => ({
  background: 'none', border: 'none', cursor: 'pointer',
  color, padding: '6px 8px', borderRadius: '4px',
});
const modalBackdrop = () => ({
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
});
const modalCard = () => ({
  backgroundColor: 'white', padding: '28px', borderRadius: '12px',
  width: '90%', maxWidth: '480px',
});

export default GrowerManagement;
